import { runConformance, type ConformanceOptions, type ConformanceResult, type ConformanceTarget } from '@casa/bridge';
import { runSandboxCheck, type SandboxCheckResult } from '../drivers/sandbox_check.ts';
import { bridge } from './native_bridge.ts';

// Lets scripts/conformance-report.mjs run the bridge conformance suite in this shell, through the
// DevTools port (Electron, Android) or the iOS debug remote: `__casaConformance.run(target)`, and
// the driver sandbox check of Phase 1 C4: `__casaConformance.sandbox(target)`.

export interface ConformanceHook {
    run(target: ConformanceTarget, options?: ConformanceOptions) : Promise<ConformanceResult[]>;
    sandbox(target: ConformanceTarget) : Promise<SandboxCheckResult[]>;
    /** The lifecycle events received since the page loaded, oldest first (the suite cannot send the app to the background itself). */
    lifecycleEvents() : { state: 'foreground' | 'background'; at: number }[];
}

const lifecycleEvents: { state: 'foreground' | 'background'; at: number }[] = [];
bridge?.lifecycle.onForeground(() => lifecycleEvents.push({ state: 'foreground', at: Date.now() }));
bridge?.lifecycle.onBackground(() => lifecycleEvents.push({ state: 'background', at: Date.now() }));

const hook: ConformanceHook = {
    async run(target, options) {
        if (!bridge) {
            throw new Error('no native bridge in this host');
        }

        return runConformance(bridge, target, options);
    },
    async sandbox(target) {
        if (!bridge) {
            throw new Error('no native bridge in this host');
        }

        return runSandboxCheck(bridge, target);
    },
    lifecycleEvents: () => [...lifecycleEvents],
};

(globalThis as unknown as { __casaConformance?: ConformanceHook }).__casaConformance = hook;
